/* eslint-disable camelcase */
import { handle } from 'redux-pack';
import {
	AUTO_START_BROADCAST,
	GET_BROADCAST_SERVER,
	GET_DB_STREAM_STATE,
	MANUALLY_CREATE_BROADCAST_SERVER,
	RESET_BROADCAST_REDUCER_STATE,
	RESET_BROADCAST_SERVER,
	SET_BROADCAST_STATE,
	SET_BROADCAST_STATS,
	SET_DB_STREAM_STATE,
	SET_GO_LIVE_BUTTON_CLICKED,
	SET_PREVIOUS_BROADCAST_STATE,
	SET_RECORDING_OPTION,
	SET_STREAM_STATE,
	START_BROADCAST,
	STOP_BROADCAST,
	SYNC_BROADCAST_REDUCER_STATE,
	SET_BROADCAST_STARTING_SHOT,
	SET_BROADCAST_STARTED_BY,
	SET_CURRENT_RECORDING_STREAM_ID,
	SET_GO_LIVE_FROM_MODAL,
	SET_ADDING_DESTINATION,
	SET_EDITING_DESTINATION,
	START_SPIELBERG_BROADCAST,
	STOP_SPIELBERG_BROADCAST,
	ADD_DESTINATION,
	UPDATE_DESTINATION,
	DELETE_DESTINATION,
} from '../actions/broadcasting';

import { Action } from '../types/types';
import { EntityType } from '../../types/enums';
import * as T from '../types/broadcasting';
import type { BroadcastingState } from '../types/broadcasting';

export const initialBroadcastingState: BroadcastingState = {
	server: null,
	loadingBroadcastServer: false,
	loadingBroadcastServerError: false,
	broadcasting: false,
	startingBroadcast: false,
	failedToStartBroadcast: false,
	stoppingBroadcast: false,
	failedToStopBroadcast: false,
	serverState: 0,
	streamState: 2,
	previousServerState: 0,
	stats: {
		bitrate: 0,
		fps: 0,
		health: 0,
	},
	goLiveButtonClicked: false,
	selectedRecordingOption: 0,
	broadcastStartingShotId: 0,
	broadcastStartedBy: 0,
	currentRecordingStreamId: '',
	broadcastWarnings: [],
	goLiveFromModal: false,
	addingDestination: null,
	editingDestination: '',
	savingDestination: false,
	spielbergBroadcast: null,
	destinations: [],
	resolution: '1080p',
	destinationStatus: [],
};

const deriveDestinationStatus = (
	outputs: T.StreamInfo[],
	destinations: T.Destination[],
): T.DestinationStatus[] => outputs.reduce((acc: T.DestinationStatus[], curr: T.StreamInfo) => {
	const [domain, redactedStreamKey] = curr.url.split(/\/(?=[^/]*$)/);
	// stream keys returned from LiveKit only retain the first and last 3 characters of the key
	const keyStartAndEnd = redactedStreamKey.replace(/[{}]/g, '').split('...');
	const matchingDestination = destinations.find((destination) => (
		// LiveKit modifies Twitch and Mux urls, but leaves others alone
		(destination.url === domain
			|| (destination.url.includes('contribute.live-video.net') && domain.includes('contribute.live-video.net'))
			|| (destination.url.includes('global-live.mux.com') && domain.includes('global-live.mux.com'))
		)
		&& destination.streamKey.startsWith(keyStartAndEnd[0])
		&& destination.streamKey.endsWith(keyStartAndEnd[1])));
	if (matchingDestination) {
		acc.push({
			id: matchingDestination.id ?? 0, // 0 is blp destination
			name: matchingDestination.name ?? '',
			type: matchingDestination.type ?? 0,
			logo: matchingDestination.logo ?? '',
			status: curr.status,
			error: curr.error,
		});
	}
	return acc;
}, []);

const actions: Map<any, Function> = new Map([
	[GET_BROADCAST_SERVER, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, loadingBroadcastServer: true }),
		failure: (pState) => ({ ...pState, loadingBroadcastServerError: true }),
		success: (pState) => {
			if (!action.payload) {
				return pState;
			}

			const newState = { ...pState };
			if ('error' in action.payload) {
				newState.serverState = 0;
			} else if ('starting_up' in action.payload) {
				newState.serverState = T.BROADCAST_STATES.STARTING_UP;
			} else if (action.payload.id) {
				newState.server = action.payload;
				newState.serverState = action.payload.current_state;
				newState.broadcasting = action.payload.current_state === T.BROADCAST_STATES.BROADCAST_STARTED;
			}

			return newState;
		},
		finish: (pState) => ({ ...pState, loadingBroadcastServer: false }),
	})],
	[START_BROADCAST, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, startingBroadcast: true }),
		failure: (pState) => ({ ...pState, failedToStartBroadcast: true }),
		success: (pState) => {
			let broadcastWarnings = [];
			if (action.payload
				&& action.payload.broadcastWarnings) {
				broadcastWarnings = action.payload.broadcastWarnings;
			}
			return {
				...pState,
				broadcastWarnings,
				broadcasting: true,
				failedToStartBroadcast: false,
			};
		},
		finish: (pState) => ({ ...pState, startingBroadcast: false }),
	})],
	[MANUALLY_CREATE_BROADCAST_SERVER,
		(state: BroadcastingState, action: Action) => handle(state, action, {
			start: (pState) => ({ ...pState, startingBroadcast: true }),
			failure: (pState) => ({ ...pState, failedToStartBroadcast: true }),
			success: (pState) => ({ ...pState, broadcasting: true, failedToStartBroadcast: false }),
			finish: (pState) => ({ ...pState, startingBroadcast: false }),
		}),
	],
	[STOP_BROADCAST, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, stoppingBroadcast: true }),
		failure: (pState) => ({ ...pState, failedToStopBroadcast: true }),
		success: (pState) => ({ ...pState, broadcasting: false, failedToStopBroadcast: false }),
		finish: (pState) => ({ ...pState, stoppingBroadcast: false }),
	})],
	[SET_BROADCAST_STATE, (state: BroadcastingState, action: Action) => (
		{ ...state, serverState: action.payload }
	)],
	[SET_STREAM_STATE, (state: BroadcastingState, action: Action): BroadcastingState => (
		{ ...state, streamState: action.payload }
	)],
	[SET_PREVIOUS_BROADCAST_STATE, (state: BroadcastingState, action: Action): BroadcastingState => (
		{ ...state, previousServerState: action.payload }
	)],
	[SET_BROADCAST_STATS, (state: BroadcastingState, action: Action): BroadcastingState => (
		{ ...state, stats: action.payload }
	)],
	[AUTO_START_BROADCAST, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, startingBroadcast: true }),
		failure: (pState) => ({ ...pState, failedToStartBroadcast: true }),
		success: (pState) => ({ ...pState, broadcasting: true, failedToStartBroadcast: false }),
		finish: (pState) => ({ ...pState, startingBroadcast: false }),
	})],
	[SET_DB_STREAM_STATE, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, settingStreamState: true }),
		failure: (pState) => ({ ...pState, failedToSetStreamState: true }),
		success: (pState) => ({ ...pState, failedToSetStreamState: false }),
		finish: (pState) => ({ ...pState, settingStreamState: false }),
	})],
	[GET_DB_STREAM_STATE, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, gettingStreamState: true }),
		failure: (pState) => ({ ...pState, failedToGetStreamState: true }),
		success: (pState) => ({ ...pState, failedToGetStreamState: false }),
		finish: (pState) => ({
			...pState, gettingStreamState: false, streamState: action.payload.streamState,
		}),
	})],
	[SET_GO_LIVE_BUTTON_CLICKED, (state: BroadcastingState, action: Action) => (
		{ ...state, goLiveButtonClicked: action.payload }
	)],
	[RESET_BROADCAST_SERVER, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, loadingBroadcastServer: true }),
		failure: (pState) => ({ ...pState, loadingBroadcastServerError: true }),
		success: (pState) => {
			const newState = { ...pState };
			if ('error' in action.payload) {
				newState.serverState = 0;
			} else if ('starting_up' in action.payload) {
				newState.serverState = T.BROADCAST_STATES.STARTING_UP;
			} else if (action.payload.id) {
				newState.server = action.payload;
				newState.serverState = action.payload.current_state;
				newState.broadcasting = action.payload.current_state === T.BROADCAST_STATES.BROADCAST_STARTED;
			}

			return newState;
		},
		finish: (pState) => ({ ...pState, loadingBroadcastServer: false }),
	})],
	[SET_RECORDING_OPTION, (state: BroadcastingState, action: Action) => (
		{ ...state, selectedRecordingOption: action.payload }
	)],
	[SYNC_BROADCAST_REDUCER_STATE, (state: BroadcastingState, action: Action) => ({
		...state,
		...action.payload,
	})],
	[RESET_BROADCAST_REDUCER_STATE, () => (
		{ ...initialBroadcastingState }
	)],
	[SET_BROADCAST_STARTING_SHOT, (state: BroadcastingState, action: Action) => (
		{ ...state, broadcastStartingShotId: action.payload }
	)],
	[SET_BROADCAST_STARTED_BY, (state: BroadcastingState, action: Action) => (
		{ ...state, broadcastStartedBy: action.payload }
	)],
	[SET_CURRENT_RECORDING_STREAM_ID, (state: BroadcastingState, action: Action) => (
		{ ...state, currentRecordingStreamId: action.payload }
	)],
	[SET_GO_LIVE_FROM_MODAL, (state: BroadcastingState, action: Action) => ({
		...state,
		goLiveFromModal: action.payload,
	})],
	[SET_ADDING_DESTINATION, (state: BroadcastingState, action: Action) => (
		{ ...state, addingDestination: action.payload }
	)],
	[SET_EDITING_DESTINATION, (state: BroadcastingState, action: Action) => (
		{ ...state, editingDestination: action.payload }
	)],
	[START_SPIELBERG_BROADCAST, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (s) => ({ ...s, startingBroadcast: true }),
		finish: (s) => ({ ...s, startingBroadcast: false }),
		failure: (s) => ({ ...s }),
		success: (s) => ({ ...s }),
	})],
	[STOP_SPIELBERG_BROADCAST, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (s) => ({ ...s, stoppingBroadcast: true }),
		finish: (s) => ({ ...s, stoppingBroadcast: false }),
		failure: (s) => ({ ...s }),
		success: (s) => ({ ...s }),
	})],
	[EntityType.broadcast, (state: BroadcastingState, action: Action) => ({
		...state,
		spielbergBroadcast: action.payload?.entityValue?.broadcast ?? null,
		destinationStatus: deriveDestinationStatus(
			action.payload?.entityValue?.broadcast?.outputs ?? [],
			state.destinations,
		),
	})],
	[EntityType.destinations, (state: BroadcastingState, action: Action) => ({
		...state,
		destinations: action.payload?.entityValue?.destinations ?? [],
		resolution: action.payload?.entityValue?.resolution ?? '1080p',
		addingDestination: state.savingDestination ? null : state.addingDestination,
		editingDestination: state.savingDestination ? '' : state.editingDestination,
		savingDestination: false,
	})],
	[ADD_DESTINATION, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (s) => ({ ...s, savingDestination: true }),
		finish: (s) => ({ ...s, savingDestination: false }),
		failure: (s) => ({ ...s }),
		success: (s) => ({
			...s,
			destinations: action.payload.entityValue.destinations,
			addingDestination: null,
			savingDestination: false,
		}),
	})],
	[UPDATE_DESTINATION, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (s) => ({ ...s, savingDestination: true }),
		finish: (s) => ({ ...s, savingDestination: false }),
		failure: (s) => ({ ...s }),
		success: (s) => ({
			...s,
			destinations: action.payload.entityValue.destinations,
			editingDestination: '',
			savingDestination: false,
		}),
	})],
	[DELETE_DESTINATION, (state: BroadcastingState, action: Action) => handle(state, action, {
		start: (s) => ({ ...s, savingDestination: true }),
		finish: (s) => ({ ...s, savingDestination: false }),
		failure: (s) => ({ ...s }),
		success: (s) => ({
			...s,
			destinations: action.payload.entityValue.destinations,
			editingDestination: '',
			savingDestination: false,
		}),
	})],
]);

// eslint-disable-next-line default-param-last, max-len
export function BroadcastReducer(state: BroadcastingState = initialBroadcastingState, action: Action) {
	return actions.get(action.type)?.(state, action) || state;
}
